import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { App, Button, Card, DatePicker, Drawer, Form, Input, Select, Space, Switch, Table, Tag, Typography } from 'antd';
import { SearchOutlined, ReloadOutlined, EditOutlined } from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import { get, patch } from '../../../api/client';
import { LEVEL_COLOR, type SubscriptionRow, type SubscriptionListResponse, type LevelRow } from '../types';

const STATUS_COLOR: Record<string, string> = { 激活: 'green', 禁用: 'red', 已合并: 'default', 待验证: 'orange' };

/**
 * 订阅用户管理：分页检索 + 手工调整（等级 / 到期 / 自动续费）。
 * - GET /admin/membership/subscriptions：query 一律 camelCase（keyword / levelCode / page / pageSize）。
 *   query 不经任何大小写转换，键名必须与后端 ListSubscriptionsQueryDto 逐字一致。
 * - PATCH /admin/membership/subscriptions/:userId：请求体 camelCase，由 RequestInterceptor 转 snake_case 绑定 DTO。
 */
export function SubscriptionsTab() {
  const { message } = App.useApp();
  const qc = useQueryClient();

  const [keyword, setKeyword] = useState('');
  const [levelCode, setLevelCode] = useState<string | undefined>();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  // 已提交的检索条件（点击查询后才生效，避免每次输入都请求）
  const [committed, setCommitted] = useState<{ keyword?: string; levelCode?: string }>({});

  const [adjusting, setAdjusting] = useState<SubscriptionRow | null>(null);
  const [form] = Form.useForm();

  const { data: levelData } = useQuery({
    queryKey: ['membership-levels'],
    queryFn: () => get<LevelRow[]>('/admin/membership/levels'),
  });
  const levels = levelData ?? [];

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['membership-subscriptions', committed, page, pageSize],
    queryFn: () =>
      get<SubscriptionListResponse>('/admin/membership/subscriptions', {
        ...committed,
        page,
        pageSize,
      }),
  });

  const doSearch = () => {
    setPage(1);
    setCommitted({
      ...(keyword.trim() ? { keyword: keyword.trim() } : {}),
      ...(levelCode ? { levelCode } : {}),
    });
  };

  const resetSearch = () => {
    setKeyword('');
    setLevelCode(undefined);
    setPage(1);
    setCommitted({});
  };

  const saveAdjust = useMutation({
    mutationFn: ({ userId, values }: { userId: string; values: Record<string, unknown> }) =>
      patch(`/admin/membership/subscriptions/${userId}`, values),
    onSuccess: () => {
      message.success('用户订阅已调整');
      setAdjusting(null);
      void qc.invalidateQueries({ queryKey: ['membership-subscriptions'] });
    },
    onError: (e) => message.error(e instanceof Error ? e.message : '调整失败'),
  });

  const openAdjust = (row: SubscriptionRow) => {
    setAdjusting(row);
    form.setFieldsValue({
      levelCode: row.levelCode,
      levelExpireAt: row.expireAt ? dayjs(row.expireAt) : null,
      autoRenew: row.autoRenew,
    });
  };

  const submitAdjust = async () => {
    if (!adjusting) return;
    try {
      const v = await form.validateFields();
      saveAdjust.mutate({
        userId: adjusting.id,
        values: {
          levelCode: v.levelCode,
          // 清空日期 = 长期有效（无到期），后端置 null
          levelExpireAt: (v.levelExpireAt as Dayjs | null) ? (v.levelExpireAt as Dayjs).toISOString() : null,
          autoRenew: v.autoRenew ?? false,
        },
      });
    } catch {
      /* 校验失败 */
    }
  };

  return (
    <Card
      title="订阅用户"
      extra={
        <Space wrap>
          <Input
            allowClear
            placeholder="搜索用户名 / 姓名"
            prefix={<SearchOutlined style={{ color: '#b6bcc2' }} />}
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onPressEnter={doSearch}
            style={{ width: 200 }}
          />
          <Select
            allowClear
            placeholder="按等级筛选"
            value={levelCode}
            onChange={setLevelCode}
            style={{ width: 150 }}
            options={levels.map((l) => ({ value: l.code, label: l.name }))}
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={doSearch}>
            查询
          </Button>
          <Button icon={<ReloadOutlined />} onClick={resetSearch}>
            重置
          </Button>
        </Space>
      }
    >
      <Table<SubscriptionRow>
        rowKey="id"
        size="middle"
        loading={isLoading || isFetching}
        dataSource={data?.items ?? []}
        pagination={{
          current: data?.page ?? page,
          pageSize: data?.pageSize ?? pageSize,
          total: data?.total ?? 0,
          showSizeChanger: true,
          showTotal: (t) => `共 ${t} 位用户`,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        }}
        columns={[
          {
            title: '用户',
            key: 'user',
            render: (_, r) => (
              <Space direction="vertical" size={0}>
                <Typography.Text strong>{r.fullName || '—'}</Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {r.username || '无用户名'}
                </Typography.Text>
              </Space>
            ),
          },
          {
            title: '当前等级',
            key: 'level',
            width: 160,
            render: (_, r) => (
              <Space direction="vertical" size={0}>
                <Tag color={LEVEL_COLOR[r.levelCode] ?? 'blue'}>{r.levelName}</Tag>
                <Typography.Text type="secondary" style={{ fontSize: 11, fontFamily: 'monospace' }}>
                  {r.levelCode}
                </Typography.Text>
              </Space>
            ),
          },
          {
            title: '到期时间',
            dataIndex: 'expireAt',
            width: 180,
            render: (v: string | null) =>
              v ? (
                <span>{dayjs(v).format('YYYY-MM-DD HH:mm')}</span>
              ) : (
                <Tag color="teal">长期有效</Tag>
              ),
          },
          {
            title: '自动续费',
            dataIndex: 'autoRenew',
            width: 100,
            render: (v: boolean) => (v ? <Tag color="green">已开启</Tag> : <Tag>未开启</Tag>),
          },
          {
            title: '账号状态',
            dataIndex: 'status',
            width: 100,
            render: (v: string | null) => <Tag color={STATUS_COLOR[v ?? ''] ?? 'default'}>{v ?? '—'}</Tag>,
          },
          {
            title: '操作',
            key: 'action',
            width: 100,
            fixed: 'right',
            render: (_, r) => (
              <Button size="small" icon={<EditOutlined />} onClick={() => openAdjust(r)}>
                调整
              </Button>
            ),
          },
        ]}
        scroll={{ x: 900 }}
      />

      <Drawer
        title={adjusting ? `调整订阅 · ${adjusting.fullName || adjusting.username || ''}` : '调整订阅'}
        open={adjusting !== null}
        onClose={() => setAdjusting(null)}
        width={420}
        extra={
          <Space>
            <Button onClick={() => setAdjusting(null)}>取消</Button>
            <Button type="primary" loading={saveAdjust.isPending} onClick={submitAdjust}>
              保存
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item name="levelCode" label="会员等级" rules={[{ required: true, message: '请选择等级' }]}>
            <Select
              placeholder="选择等级"
              options={levels.map((l) => ({ value: l.code, label: `${l.name}（${l.code}）` }))}
            />
          </Form.Item>
          <Form.Item
            name="levelExpireAt"
            label="到期时间"
            tooltip="清空表示长期有效（无到期时间）"
          >
            <DatePicker style={{ width: '100%' }} showTime placeholder="选择到期时间，留空为长期有效" />
          </Form.Item>
          <Form.Item name="autoRenew" label="自动续费" valuePropName="checked" tooltip="代扣签约为预留能力，此处仅记录开关状态">
            <Switch />
          </Form.Item>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            调整后该用户的功能权限缓存将立即失效并重新计算。
          </Typography.Text>
        </Form>
      </Drawer>
    </Card>
  );
}
